import { PetConversationResponseDtoDocumentSourcesInner } from 'woofwoof-api';

interface CachedResponseData {
  content: string;
  generatedAt?: string;
  differentialDiagnoses?: string[] | null;
  suggestedTests?: string[] | null;
  treatmentRecommendations?: string[] | null;
  warnings?: string[] | null;
  documentSources?: PetConversationResponseDtoDocumentSourcesInner[] | null;
}

interface CachedExchange {
  question: string;
  response: CachedResponseData;
}

interface ConversationMessage {
  role: 'professional' | 'assistant';
  content: string;
}

export interface AssistantChatCacheData {
  initialSummary: CachedResponseData;
  exchanges: CachedExchange[];
  conversationHistory: ConversationMessage[];
}

// Persisted threads are bounded two ways so localStorage can't grow without
// bound for a busy professional (~200 visits/month):
//  - TTL: a thread expires this long after its last write (each write refreshes
//    it, so an active thread never expires under you).
//  - CAP: at most MAX_ENTRIES threads are kept; once over, the least-recently
//    written ones (oldest savedAt) are evicted first. The cap is the hard
//    guarantee — the TTL alone wouldn't stop a burst of visits piling up.
const TTL_MS = 14 * 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 50;
const KEY_PREFIX = 'vetsi-chat:';

// On disk we wrap the data with a savedAt stamp for TTL/LRU bookkeeping.
// Entries written before this field existed are treated as expired.
interface StoredAssistantChatCache extends AssistantChatCacheData {
  savedAt: number;
}

// Threads are keyed by the signed-in storage scope (account, user,
// organization and role) so no other context can read or extend them, then by
// `profileId:visitId`. The profile-wide chat (no pinned visit) has an empty
// visit segment; guest visits have an empty profileId. Keys written before the
// scope existed lack SCOPED_PREFIX and are dropped on the next sweep.
const SCOPED_PREFIX = `${KEY_PREFIX}v2:`;

function getCacheKey(
  scope: string,
  profileId: string,
  visitId: string | undefined,
): string {
  return `${SCOPED_PREFIX}${scope}:${profileId}:${visitId ?? ''}`;
}

function isExpired(savedAt: unknown): boolean {
  return typeof savedAt !== 'number' || Date.now() - savedAt > TTL_MS;
}

// All persisted threads with their savedAt. Legacy/unparseable entries get
// -Infinity so they sort as the oldest and read as expired.
function listEntries(): { key: string; savedAt: number }[] {
  const entries: { key: string; savedAt: number }[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(KEY_PREFIX)) continue;
    let savedAt = Number.NEGATIVE_INFINITY;
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw
        ? (JSON.parse(raw) as Partial<StoredAssistantChatCache>)
        : null;
      if (parsed && typeof parsed.savedAt === 'number') {
        savedAt = parsed.savedAt;
      }
    } catch {
      // keep -Infinity → treated as oldest / expired
    }
    entries.push({ key, savedAt });
  }
  return entries;
}

// Drop expired and unscoped threads, then evict the oldest beyond MAX_ENTRIES.
function enforceBudget(): void {
  try {
    const survivors: { key: string; savedAt: number }[] = [];
    listEntries().forEach((entry) => {
      if (!entry.key.startsWith(SCOPED_PREFIX) || isExpired(entry.savedAt)) {
        localStorage.removeItem(entry.key);
      } else {
        survivors.push(entry);
      }
    });
    if (survivors.length > MAX_ENTRIES) {
      survivors
        .sort((a, b) => b.savedAt - a.savedAt) // newest first
        .slice(MAX_ENTRIES) // everything past the cap = oldest
        .forEach((entry) => localStorage.removeItem(entry.key));
    }
  } catch {
    // localStorage unavailable
  }
}

export function getAssistantChatCache(
  scope: string,
  profileId: string,
  visitId: string | undefined,
): AssistantChatCacheData | null {
  try {
    const key = getCacheKey(scope, profileId, visitId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredAssistantChatCache>;
    if (isExpired(parsed.savedAt)) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed as AssistantChatCacheData;
  } catch {
    return null;
  }
}

export function setAssistantChatCache(
  scope: string,
  profileId: string,
  visitId: string | undefined,
  data: AssistantChatCacheData,
): void {
  try {
    const stored: StoredAssistantChatCache = { ...data, savedAt: Date.now() };
    localStorage.setItem(
      getCacheKey(scope, profileId, visitId),
      JSON.stringify(stored),
    );
  } catch {
    // localStorage full or unavailable
    return;
  }
  // Keep the store within its TTL + size budget after each write.
  enforceBudget();
}

export function clearAssistantChatCache(
  scope: string,
  profileId: string,
  visitId: string | undefined,
): void {
  try {
    localStorage.removeItem(getCacheKey(scope, profileId, visitId));
  } catch {
    // localStorage unavailable
  }
}

/**
 * Drop every persisted assistant thread, of any scope: clinical content must
 * not stay in the browser after a context change or logout.
 */
export function clearAllAssistantChatCaches(): void {
  try {
    listEntries().forEach((entry) => localStorage.removeItem(entry.key));
  } catch {
    // localStorage unavailable
  }
}

/**
 * Sweep persisted assistant threads: drop expired/unparseable ones and evict
 * the oldest beyond the size cap. Read-time expiry only cleans entries you
 * reopen; this reclaims space for visits that are never revisited. Safe to call
 * on mount.
 */
export function pruneAssistantChatCache(): void {
  enforceBudget();
}
